import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  debounce,
  Dialog,
  Grid,
  Snackbar,
  Typography,
} from '@mui/material';
import DataPipelineHeader from './DataPipelineHeader';
import DashboardIcon from '../../assets/material-symbols_dashboard.svg';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import {
  getProgramSectorList,
  getProjectListByProgram,
  getSectorProgramMapping,
} from 'service/service';
import TitleStatus from 'components/IBPDataPipeline/TitleStatus';
import { ReactComponent as EditIcon } from '../../assets/edit-img.svg';
import { ReactComponent as DeleteIcon } from '../../assets/delete-img.svg';
import Loader from 'components/Loader/loader';
import { DataPipelineModal } from '../IBPDataPipeline/modal-component';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import RowTypography from 'components/Table/RowTypography';
import { useLocation, useNavigate } from 'react-router-dom';
import { checkRole } from 'utils/roleUtils';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PageWrapper from 'components/Common/PageWraper';
import colors from 'assets/theme/base/colors';
import {
  getBackgroundSx,
  getSearchInputLabelProps,
  searchInputSx,
  setRdgTableHeight,
} from '../IBPDataPipeline/DataPipelineHelper';
import {
  ipDelayedStartFormatter,
  ipLongRunningFormatter,
  jobsFailedFormatter,
  jobsSuccessFormatter,
  projectLinkFormatter,
  projectSlaStatusFormatter,
  slaDateTimeFormatter,
} from '../IBPDataPipeline/DataPipelineFormatter';

const NewDashboardIBP = () => {
  const location = useLocation();

  const [openModal, setOpenModal] = useState<any>({ isOpen: false });
  const [programList, setProgramList] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [currentProgram, setCurrentProgram] = useState<any>({});
  const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
  const [projectList, setProjectList] = useState<any>([]);
  const page = 1;
  const pageSize = 100;
  const [controller] = usePepwiseAuthController();
  const { role } = controller;
  const defaultColumnNames = [
    'Sectors',
    'Project',
    'Delayed Start',
    'Long Running',
    'Failed',
    'Success',
    'SLA Status',
    'SLA Time (CST)',
  ];
  const filterData: any[] = [];
  const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
  const searchDetails: any[] = [];
  const filterInfo: any[] = [];
  const [sectorMasterList, setSectorMasterList] = useState<any[]>([]);
  const [searchProgram, setSearchProgram] = useState('');
  const [toast, setToast] = useState<any>({
    open: false,
    message: '',
  });

  const [hasAccess, setHasAccess] = useState(true); 
  const [expandedSector, setExpandedSector] = useState(null);
  const handleAccordionChange = (sector: any) => (event: any, isExpanded: any) => {
    setExpandedSector(isExpanded ? sector : null);
  };
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [ibpFilterValues, setIbpFilterValues] = useState<Record<string, string>>({});
  const open = Boolean(anchorEl);
  const isAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User']);
  const navigate = useNavigate();
  const getColumns = () => {
    return [
      {
        key: 'Sectors',
        name: 'Sectors',
        formatter(props: any) {
          const sectorList = props.row.Sectors ? props.row.Sectors.split(', ') : [];
          if (sectorList && sectorList.length) {
            return sectorList.map((sector: string, idx: number) => (
              <RowTypography
                sx={{
                  backgroundColor: 'backgroundLight.dark',
                  color: 'white.main',
                  padding: '4px 10px',
                  marginRight: '5px',
                  borderRadius: '20px',
                }}
                key={sector}
                title={sector}
              >
                {sector}
              </RowTypography>
            ));
          } else {
            return '--';
          }
        },
        minWidth: 150,
        maxWidth: 250,
      },
      {
        key: 'Project_Name',
        name: 'Project',
        formatter: projectLinkFormatter(currentProgram.Program_Name),
        minWidth: 200,
        maxWidth: 500,
      },
      {
        key: 'IP_Delayed_Start',
        name: 'Delayed Start',
        formatter: ipDelayedStartFormatter(),
        minWidth: 100,
      },
      {
        key: 'IP_Long_Running',
        name: 'Long Running',
        formatter: ipLongRunningFormatter(),
        minWidth: 100,
      },
      {
        key: 'Failed',
        name: 'Failed',
        formatter: jobsFailedFormatter(),
        minWidth: 100,
      },
      {
        key: 'Success',
        name: 'Success',
        formatter: jobsSuccessFormatter(),
        minWidth: 100,
      },
      {
        key: 'SLA_Status',
        name: 'SLA Status',
        formatter: projectSlaStatusFormatter(),
        minWidth: 150,
      },
      {
        key: 'SLA_Time',
        name: 'SLA Time (CST)',
        formatter: slaDateTimeFormatter(),
        minWidth: 150,
      },
    ];
  };

  const handleFilter = (e: any) => {
    console.log('FILTER', e);
  };

  const handleSearch = (e: any) => {
    console.log('FILTER', e);
  };

  const handleSorting = (sortingInfo: any) => {
    setSortingInfo(sortingInfo);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    getProjectsByProgram(currentProgram, value, pageSize);
  };

  const getToastData = (data: any) => {
    setToast({
      ...toast,
      message: data.data.message,
    });
  };

  const handleClose = () => {
    setToast({
      ...toast,
      open: false,
    });
  };

  const handlepageSizeChange = (e: any) => {
    getProjectsByProgram(currentProgram, page, e.target.value);
  };

  const onCreateProgramClick = () => {
    // navigate(`/data-pipeline/programs-tab`);
    navigate(`/data-pipeline/ibp-dashboard/programs-tab`);
  };

  const handleDetailsThreeDotsClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };
  const handleDetailsThreeDotsClose = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleProjectSLA = (programData: any, filterType: string) => {
    getProjectsByProgram(programData, 1, 100, filterType, getSectorId());
  };

  const onClickAddProject = (event: any, prgId: string, prgName: string) => {
    setOpenModal({
      ...openModal,
      isOpen: true,
      type: 'Project',
      action: 'CREATE',
      data: { ProgramID: prgId },
    });
    navigate(
      `/data-pipeline/programs-tab?programID=${prgId}&programName=${prgName}&tab=1&type=Project&isEdited=true`,
    );
  };

  const handleEditClose = useCallback((closeType: any) => {
    setOpenModal({
      isOpen: false,
      closeType: closeType,
    });
  }, []);

  const handleAction = (module: string, type: string, data: any, e: any) => {
    e.preventDefault();
    e.stopPropagation();
    handleDetailsThreeDotsClose(e);
    data = {
      ...data,
      ProgramName: data.Program_Name,
      ProgramID: data.Program_id,
    };
    switch (type) {
      case 'edit':
        setOpenModal({
          isOpen: true,
          type: module,
          action: 'EDIT',
          data: data,
        });
        navigate(
          `/data-pipeline/programs-tab?programID=${data.Program_id}&programName=${data.Program_Name}&isEdited=true`,
        );
        break;
      case 'delete':
        setOpenModal({
          isOpen: true,
          type: module,
          action: 'DELETE',
          data: data,
        });
        break;
      default:
        break;
    }
  };

    const getProgramData = useCallback(async (
    sectorId?: number,
    searchKeyword?: string,
    filters?: Record<string, string>
  ) => {
    setLoading(true);
    setHasAccess(true);
    try {
      const isIbpDashboard = location.pathname?.includes('/ibp-dashboard');
      const apiFilters = isIbpDashboard ? mapIbpFiltersForApi(filters) : undefined;
      let data: any = await getSectorProgramMapping(searchKeyword, undefined, apiFilters, true);
      if(data.status === 403) {
        setHasAccess(false);
        return;
      }
      setLoading(false);

      if (isIbpDashboard) {
        // TESTING ONLY: force dashboard to show only BNL bucket
        const marketsToShow = ['BNL', 'FRANCE'];
        const filteredData: Record<string, any> = {};
        marketsToShow.forEach(market => {
            if (data?.data?.[market]) {
                filteredData[market] = data.data[market];
            }
        });
        setProgramList(Object.keys(filteredData).length > 0 ? filteredData : {});
    }
    } catch (error) {
      console.error('Error fetching program data:', error);
      setLoading(false);
      setProgramList({});
    }
  }, [location.pathname]);

  useEffect(() => {
    const activeId = document.getElementById('root');
    if (window.location.pathname.includes('/data-pipeline/programOps/IBP/markets/ibp-dashboard')) {
      if (activeId) {
        document.head.classList.add('theme-bg-blue');
        document.body.classList.add('theme-bg-blue');
      }
    }
    localStorage.removeItem('selectedSector');
    localStorage.removeItem('filterSectors');
    localStorage.removeItem('sectorsMasterData');
    if (localStorage.getItem('filterSectors') && localStorage.getItem('sectorsMasterData')) {
      setSectorMasterList(JSON.parse(localStorage.getItem('sectorsMasterData') ?? ''));
    } else {
      getSectorData();
    }
    getProgramData(getSectorId());
  }, []);

    useEffect(() => {
        if (openModal && !openModal.isOpen && openModal.closeType && openModal.closeType === 'updated') {
            if (location.pathname?.includes('/ibp-dashboard')) {
                getProgramData(undefined, undefined, ibpFilterValues);
            } else {
                getProgramData();
            }
            setSearchProgram('');
        }
    }, [openModal, ibpFilterValues, location.pathname, getProgramData]);

        useEffect(() => {
            if (location.pathname?.includes('/ibp-dashboard')) {
                getProgramData(undefined, undefined, ibpFilterValues);
                setProjectList([]);
            }
        }, [ibpFilterValues, location.pathname, getProgramData]);

  const getSectorId = () => {
    let sectorId;
    let selectedSctr = localStorage.getItem('selectedSector');
    if (selectedSctr && selectedSctr != 'all') {
      const sectorList = JSON.parse(localStorage.getItem('sectorsMasterData') ?? '');
      if (sectorList) {
        sectorId = sectorList.filter((s: any) => s.Sector_Name == selectedSctr)[0].Sector_Id;
      } else if (sectorMasterList) {
        sectorId = sectorMasterList.filter((s: any) => s.Sector_Name == selectedSctr)[0].Sector_Id;
      }
    }
    return sectorId;
  };

  const mapIbpFiltersForApi = (values?: Record<string, string>) => {
    const mapped: Record<string, string> = {};
    if (!values) return mapped;

    Object.entries(values).forEach(([key, value]) => {
      const mappedKey =
        key === 'gdo'
          ? 'Program_name'
          : key === 'gdo_status'
          ? 'Sla_Status'
          : key === 'market'
          ? 'projectSector'
          : key;
      mapped[mappedKey] = value;
    });

    return mapped;
  };


  const debouncedFetchOptions = useMemo(
    () =>
      debounce(async (searchText: string) => {
        try {
          getProgramData(getSectorId(), searchText);
          setProjectList([]);
        } catch (error) {
          console.error(error);
        }
      }, 500),
    [getSectorId],
  );

  const handleSearchData = (searchText: string) => {
    setSearchProgram(searchText);
    debouncedFetchOptions(searchText);
  };
  const getSectorData = async () => {
    try {
      let sectors: any = await getProgramSectorList();
      if (sectors?.data?.sectors && Array.isArray(sectors.data.sectors)) {
        const filterSectors = sectors.data.sectors.map((sec: any) => sec.Sector_Name);
        localStorage.setItem('filterSectors', JSON.stringify(filterSectors));
        localStorage.setItem('sectorsMasterData', JSON.stringify(sectors.data.sectors));
        setSectorMasterList(sectors.data.sectors);
      }
    } catch (error) {
      console.error('Error fetching sector data:', error);
    }
  };

  const handleIbpFiltersChange = useCallback((nextValues: Record<string, string>) => {
    setIbpFilterValues(nextValues);
  }, []);


  const getProjectsByProgram = async (
    data: any,
    page: number,
    pageSize: number,
    filterType?: string,
    sectorId?: number,
  ) => {
    page = page || 1;
    setTableLoading(true);
    setCurrentProgram(data);
    let result = await getProjectListByProgram(
      data.Program_id,
      page,
      pageSize,
      filterType,
      {},
      '',
      '',
      sortingInfo,
      sectorId,
    );
    setTimeout(() => {
      setRdgTableHeight(`project_list${data.Program_id}`, result.data.projectList.length);
    }, 100);
    setPaginationData(result.data.page);
    setTableLoading(false);
    const projData = result.data.projectList.map((list: any) => {
      list['activeRow'] = 0;
      const selectedSectors = list.Sectors && list.Sectors.split(', ');
      list['Sectors'] =
        selectedSectors && selectedSectors.length >= 10 ? ['All 10 Sectors'] : list.Sectors;
      return list;
    });
    setProjectList(projData);
    setTimeout(() => {
      scrollToBottom();
    }, 500);
  };
  // Type for a program object (customize fields as needed)
  type Program = {
    [key: string]: any; // or define specific fields if you want
  };

  // Type for the main object: keys are sector names, values are arrays of programs
  type ProgramList = {
    [sector: string]: Program[];
  };

  const handleDropdownClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  // Helper function to clear session storage
  const clearSessionData = () => {
    sessionStorage.removeItem('executiveTemplateTab');
    sessionStorage.removeItem('executiveTemplateDashboard');
    sessionStorage.removeItem('executiveTemplateWorkflow');
    sessionStorage.removeItem('executiveTemplateHops');
    sessionStorage.removeItem('executiveTemplateSla');
  };

  const handleExecutiveTemplate = () => {
    handleDropdownClose();
    clearSessionData();
    navigate(`/data-pipeline/executiveTemplate`);
  };

  return (
    hasAccess && <PageWrapper>
      <Box className={'main-bordered-container'} bgcolor="backgroundLight.primary">
        <DataPipelineHeader
          title="Programs Dashboard"
          icon={DashboardIcon}
          lastSync={location.pathname?.includes('/ibp-dashboard') ? "" : "Today"}
          isDataRefresh={location.pathname?.includes('/ibp-dashboard') ? false : true}
          isAllowed={isAllowed}
          anchorEl={anchorEl}
          open={open}
          onDropdownClick={handleDropdownClick}
          onDropdownClose={handleDropdownClose}
          onCreateClick={onCreateProgramClick}
          onExecutiveTemplate={handleExecutiveTemplate}
          buttonLabel={location.pathname?.includes('/ibp-dashboard') ? 'CREATE GDO' : 'CREATE PROGRAM'}
          showSearch={!location.pathname?.includes('/ibp-dashboard')}
          searchValue={searchProgram}
          onSearchChange={handleSearchData}
          showIbpFilters={isAllowed && location.pathname?.includes('/ibp-dashboard')}
          onIbpFiltersChange={handleIbpFiltersChange}
          showVersionToggle={!location.pathname?.includes('/ibp-dashboard')}
        />

        <Box p={4} mt={3} mb={3}>
          {!loading ? (
            <Grid container spacing={2}>
              {(programList && typeof programList === 'object' && !Array.isArray(programList)
                ? Object.entries(programList)
                : []
              ).map(([sector, programs]) => (
                <Accordion
                  key={sector}
                  expanded={expandedSector === sector}
                  className="program-accordian"
                  onChange={handleAccordionChange(sector)}
                  disableGutters
                  sx={{
                    backgroundColor: `${colors.backgroundLight.primary} !important`,
                    width: '100% !important',
                    boxShadow: 'none',
                    mb: expandedSector === sector ? 0 : 2,

                    '& .MuiAccordionSummary-expandIconWrapper': {
                      color: '#0099d3',
                    },
                    '& .MuiAccordionSummary-root.Mui-expanded': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                    sx={{
                      backgroundColor: 'rgba(0, 55, 113, 1) !important',
                      color: 'white',
                      px: 2,
                      minHeight: '48px !important',
                      '& .MuiAccordionSummary-content': {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        margin: 0,
                      },
                      mb: 2, // vertical spacing between rows
                    }}
                  >
                    <Box display="flex" alignItems="center" width="100%">
                      <Typography
                        variant="bodyValue"
                        noWrap
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', color: 'white.main' }}
                      >
                        {sector}
                        {(sector === 'BNL' || sector === 'FRANCE') && ' - EUROPE'}
                        <Typography
                          variant="bodyText"
                          component="span"
                          sx={{ color: '#7188ac', marginLeft: '10px' }}
                        >{`${Array.isArray(programs) ? programs.length : 0} Programs`}</Typography>
                      </Typography>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      px: 2,
                      pt: 0,
                      pb: 2,
                      border: 'none !important',
                      '&.MuiAccordionDetails-root': {
                        backgroundColor: `${colors.accordion.bg} !important`,
                      },
                    }}
                  >
                    <Grid container spacing={2}>
                      {Array.isArray(programs) && programs?.length === 0 ? (
                        <Grid item xs={12}>
                          <Typography color="textSecondary">No Programs</Typography>
                        </Grid>
                      ) : (
                        Array.isArray(programs) &&
                        programs?.map(
                          (
                            program: any, //setting all data of programs
                          ) => (
                            <Grid key={program.Program_id} container item xs={12} sm={6} md={4}>
                              <TitleStatus
                                title={program.Program_Name}
                                programId={program.Program_id}
                                isEditEnabled={isAllowed ? true : false}
                                projectCount={program.ProjectCount}
                                projectSLAs={[
                                  {
                                    label: 'SLA Missed',
                                    count: program.SLA_Missed_Count,
                                    onBtnClick: (e) => handleProjectSLA(program, 'SLA Missed'),
                                  },
                                  {
                                    label: 'SLA Met',
                                    count: program.SLA_Met_Count,
                                    onBtnClick: (e) => handleProjectSLA(program, 'SLA Met'),
                                  },
                                  {
                                    label: 'In Progress',
                                    count: program.In_Progress_Count || 0,
                                    onBtnClick: (e) => handleProjectSLA(program, 'In Progress'),
                                  },
                                  {
                                    label: 'Scheduled',
                                    count: program.Scheduled_Count || 0,
                                    onBtnClick: (e) => handleProjectSLA(program, 'Scheduled'),
                                  },
                                ]}
                                statusCount={
                                  program.SLA_Missed_Count +
                                  program.SLA_Met_Count +
                                  (program?.Scheduled_Count ? program.Scheduled_Count : 0) +
                                  (program?.In_Progress_Count ? program.In_Progress_Count : 0)
                                }
                                programSLAs={[
                                    { label: 'SLA Missed', count: program.Program_SLA_Missed_Count, onBtnClick: (e) => handleProjectSLA(program, 'SLA Missed') },
                                    { label: 'SLA Met', count: program.Program_SLA_Met_Count, onBtnClick: (e) => handleProjectSLA(program, 'SLA Met') },
                                    { label: 'In Progress', count: program.Program_In_Progress_Count || 0, onBtnClick: (e) => handleProjectSLA(program, 'In Progress') },
                                    { label: 'Scheduled', count: program.Program_Scheduled_Count || 0, onBtnClick: (e) => handleProjectSLA(program, 'Scheduled') }
                                ]}
                                programStatusCount={program.Program_SLA_Missed_Count + program.Program_SLA_Met_Count + (program?.Program_Scheduled_Count ? program.Program_Scheduled_Count : 0) + (program?.Program_In_Progress_Count ? program.Program_In_Progress_Count : 0)}
                                infoTooltip={{
                                  dateValues: [
                                    { label: 'Date Created', value: program?.Created_dt ?? '' },
                                    { label: 'Last Updated', value: program?.Updated_dt ?? '' },
                                  ],
                                }}
                                infoOptionMenu={{
                                  onClick: handleDetailsThreeDotsClick,
                                  onClose: handleDetailsThreeDotsClose,
                                  menuItems: [
                                    {
                                      key: 'edit',
                                      label: 'Edit',
                                      icon: <EditIcon />,
                                      onMenuClick: (e) =>
                                        handleAction('Program', 'edit', program, e),
                                    },
                                    {
                                      key: 'delete',
                                      label: 'Delete',
                                      icon: <DeleteIcon />,
                                      onMenuClick: (e) =>
                                        handleAction('Program', 'delete', program, e),
                                    },
                                  ],
                                  sx: { width: '20px', height: '20px' },
                                }}
                                onClickAddProject={(e) =>
                                  onClickAddProject(e, program.Program_id, program.Program_Name)
                                }
                              />
                            </Grid>
                          ),
                        )
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          ) : (
            <Loader />
          )}
        </Box>

        {projectList.length > 0 && (
          <Box p={3} sx={{ position: 'relative' }}>
            <Typography
              variant="overlayWidgetTitle"
              sx={{ color: 'textLight.main', position: 'absolute', top: 0 }}
              ref={messagesEndRef}
            >
              {currentProgram.Program_Name}
            </Typography>

            <CustomizedDataGrid
              tableColumns={getColumns()}
              tableData={projectList}
              tableType="new-dashboard"
              defaultColumnNames={defaultColumnNames}
              onSorting={handleSorting}
              currSorting={sortingInfo}
              filterDataOptions={filterData}
              searchDataValues={searchDetails}
              onFilterCheck={handleFilter}
              currFilter={filterInfo}
              onFilterSearch={handleSearch}
              page={page}
              pageSize={pageSize}
              paginationData={paginationData}
              listLength={projectList.length}
              handlepageSizeChange={handlepageSizeChange}
              handlePageChange={handlePageChange}
              isFetching={tableLoading}
              showExport={false}
              isDateRangeFilter={false}
              isDisabled={false}
              isShowMoreButton={false}
            />
          </Box>
        )}
        {tableLoading && <Loader />}

        <Dialog open={openModal.isOpen} onClose={handleEditClose} fullWidth={true} maxWidth="md">
          <DataPipelineModal
            handleEditClose={handleEditClose}
            programData={openModal}
            getToastData={getToastData}
          />
        </Dialog>
        <Snackbar
          open={toast && openModal.message}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          autoHideDuration={6000}
          onClick={handleClose}
          message={openModal.message}
        />
      </Box>
    </PageWrapper>
  );
};

export default NewDashboardIBP;
